import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Check, AlertCircle } from 'lucide-react';

/**
 * Sección colapsable con resumen de estado
 * Muestra un header compacto con indicador de campos pendientes
 */
export const CollapsibleSection = ({ 
  title, 
  children, 
  defaultOpen = false, 
  badge = null, 
  icon: Icon = null,
  summary = null, // Texto de resumen cuando está colapsado
  pendingCount = 0, // Número de campos pendientes
  completedCount = 0, // Número de campos completados
  status = null // 'complete', 'incomplete', 'empty', null
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  // Determinar estado visual
  const getStatusBadge = () => {
    if (status === 'complete' || (completedCount > 0 && pendingCount === 0)) {
      return (
        <span className="flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 text-emerald-700 text-[10px] font-medium rounded-full border border-emerald-200">
          <Check className="w-3 h-3" />
          Completo
        </span>
      );
    }
    if (status === 'incomplete' || pendingCount > 0) {
      return (
        <span className="flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 text-amber-700 text-[10px] font-medium rounded-full border border-amber-200">
          <AlertCircle className="w-3 h-3" />
          {pendingCount} pendiente{pendingCount !== 1 ? 's' : ''}
        </span>
      );
    }
    if (status === 'empty') {
      return (
        <span className="px-2.5 py-1 bg-zinc-100 text-zinc-500 text-[10px] font-medium rounded-full">
          Sin configurar
        </span>
      );
    }
    return badge ? (
      <span className="px-2.5 py-1 bg-zinc-100 text-zinc-600 text-[10px] font-medium rounded-full">
        {badge}
      </span>
    ) : null;
  };

  return (
    <div className={`border border-warm-100 rounded-xl mb-4 overflow-hidden transition-shadow ${
      isOpen ? 'shadow-sm' : 'shadow-xs hover:shadow-sm'
    }`}>
      {/* Section Header - Always Clickable */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between py-4 px-5 text-left group transition-colors ${
          isOpen ? 'bg-warm-50 border-b border-warm-100' : 'bg-white hover:bg-warm-50/50'
        }`}
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {Icon && (
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${
              isOpen ? 'bg-black text-white' : 'bg-warm-100 text-warm-600 group-hover:bg-warm-200'
            }`}>
              <Icon className="w-5 h-5" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="font-serif text-base md:text-lg text-black">{title}</h2>
              {getStatusBadge()}
            </div>
            {/* Resumen cuando está colapsado */}
            {!isOpen && summary && (
              <p className="text-xs text-warm-500 mt-1 truncate">{summary}</p>
            )}
          </div>
        </div>
        
        <div className={`ml-3 w-9 h-9 flex items-center justify-center rounded-xl transition-all flex-shrink-0 ${
          isOpen 
            ? 'bg-black text-white' 
            : 'bg-warm-100 text-warm-500 group-hover:bg-warm-200 group-hover:text-warm-700'
        }`}>
          {isOpen ? (
            <ChevronUp className="w-5 h-5" />
          ) : (
            <ChevronDown className="w-5 h-5" />
          )}
        </div>
      </button>

      {/* Section Content - Collapsible with smooth animation */}
      <div 
        className={`transition-all duration-300 ease-in-out ${
          isOpen ? 'max-h-[8000px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
        }`}
      >
        <div className="p-5 bg-white">
          {children}
        </div>
      </div>
    </div>
  );
};

/**
 * Grupo de secciones colapsables
 */
export const AccordionGroup = ({ children }) => {
  return (
    <div className="space-y-4">
      {children}
    </div>
  );
};

/**
 * Subsección colapsable más compacta
 */
export const CollapsibleSubSection = ({ 
  title, 
  children, 
  defaultOpen = true,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className={`border border-warm-100 rounded-lg overflow-hidden ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between gap-2 px-4 py-3 bg-warm-50/50 hover:bg-warm-50 transition-colors text-left"
      >
        <span className="font-medium text-warm-700 text-sm">{title}</span>
        <div className={`w-6 h-6 flex items-center justify-center rounded-lg ${
          isOpen ? 'bg-warm-200' : 'bg-warm-100'
        }`}>
          {isOpen ? (
            <ChevronUp className="w-3.5 h-3.5 text-warm-600" />
          ) : (
            <ChevronDown className="w-3.5 h-3.5 text-warm-500" />
          )}
        </div>
      </button>

      <div 
        className={`transition-all duration-200 ${
          isOpen ? 'max-h-[3000px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
        }`}
      >
        <div className="p-4 border-t border-warm-100">
          {children}
        </div>
      </div>
    </div>
  );
};
